import type {
  FieldPermissions,
  MetadataErrors,
  MdfFlavor,
  MdfSchemaNoViews,
  ViewSections,
  LayoutSection
} from './../dto/mdfModels';
import {
  getEmptyMdfViewSectionsObject,
  getEmptyMdfViewsObject,
  isButtonFieldVisibleViewType,
  isFileFieldVisibleViewType
} from './../dto/mdfModels';
import isUndefined from 'lodash/isUndefined';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import type { Dictionary } from 'lodash';
import isEqual from 'lodash/isEqual';
import type { FieldValue, ItemMetaData } from '../dto/fieldValue';
import type { Alternative, MdfFieldEntity } from './fieldDefinition';
import type {
  MdfEntity, Views, LayoutSettings
} from '../dto/mdfModels';
import { FieldType } from './fieldDefinition';
import { ViewType
} from '../dto/mdfModels';
import { invariant } from '../util/invariant';
import { isInstanceOfTimedMdfUsageRuleArray } from '../dto/timedMetadataModels';
import type { TimedMdfUsageRule } from '../dto/timedMdfUsage';
import type { OptionListDto } from '../dto/optionListDto';
import { getTreeChoiceAlternatives, getChoiceAlternatives } from '../dto/optionListDto';
import { isDateOffsetRule } from '../dto/mdfInitializationRuleTypes';
import { DEFAULT_MDF_ID, FOLDER_DEFAULT_MDF_ID } from './mdfDefaults';
import { createDefaultSettings, doesSchemaSupportView, getDateWithOffset, getEmptyMetadataFieldPermissions, isSavedField, MetaDataFieldImpl, validateChoiceValue, validateMinMaxCharacters, validateRequired, validateTreeChoiceValue, type MdfAndSettings } from './MetaDataForm';


// Some field types are not allowed in some views
// So we should not add them to view settings
// otherwise, they hide in the view settings and reordering fields becomes buggy
function shouldFieldTypeBeInView(fieldType: FieldType, viewType: ViewType) {
  if (fieldType === FieldType.Button) {
    return isButtonFieldVisibleViewType(viewType);
  }
  if (fieldType === FieldType.File) {
    return isFileFieldVisibleViewType(viewType);
  }
  if (fieldType === FieldType.JSON) {
    return viewType !== ViewType.SEARCH;
  }

  return true;
}

/**
 * Resolves the default value for a field based on priority:
 * 1. Custom schema view default value (if available)
 * 2. Schema view default value
 * 3. Field default value
 * 4. Empty string
 */
function resolveFieldDefaultValue(
  schema: MetaDataSchema,
  field: MdfFieldEntity,
  viewType: ViewType | undefined,
  customSchema: MetaDataSchema | undefined
): FieldValue {
  const customSchemaViewDefaultValue = customSchema && viewType ? customSchema.views[viewType].find(t => t.fieldId === field.fieldId)?.defaultValue : undefined;
  const viewDefaultValue = viewType ? schema.views[viewType].find(t => t.fieldId === field.fieldId)?.defaultValue : undefined;
  // MultipleChoice fields might have defaultValue empty array. If empty array is present then we should consider that undefined.
  if (field.type === FieldType.MultipleChoice) {
    if (customSchemaViewDefaultValue && !isEmpty(customSchemaViewDefaultValue)) {
      return customSchemaViewDefaultValue;
    } else if (viewDefaultValue && !isEmpty(viewDefaultValue)) {
      return viewDefaultValue;
    } else {
      return field.defaultValue ?? '';
    }
  } else {
    return customSchemaViewDefaultValue ?? viewDefaultValue ?? field.defaultValue ?? '';
  }
}

/**
 * The actual MetaDataSchema class that contains some helper methods also.
 */
export class MetaDataSchema implements MdfEntity {

  // Client only - used for caching.
  // true when the schema data has been fetched using a call such
  // getMdf or createMdf where all schema data is returned,
  // and false if the schema was fetched using a call such as getMdfs
  // where only a subset of the available per-schema data is returned
  public loaded = false;

  // Used in client to identify this MDF - these are user editable.
  // Expected format is #RRGGBBAA.
  public color?: string;
  public icon?: string;
  public compatibleWithPremiereMarker?: boolean;

  // These need to be set using Vue.set() so that they are reactive.
  public fields: MdfFieldEntity[] = [];
  public permissions: FieldPermissions = getEmptyMetadataFieldPermissions();
  public visibleTo: string[] | undefined = undefined;
  public views: Views = getEmptyMdfViewsObject();
  public viewSections: ViewSections = getEmptyMdfViewSectionsObject();
  public isSubtype?: boolean;
  public keyboardShortcut?: string;
  public duration?: number;
  public displayName?: string;

  constructor(public label: string, public readonly id: string, public active: boolean = true, public flavor?: MdfFlavor, public rule?: string, public usageRules?: TimedMdfUsageRule[]) {

  }
  /**
   * Returns the display name for the metadata schema.
   * The name is derived from the displayName property if available, otherwise it falls back to the label property.
   */
  get resolvedDisplayName(): string {
    return this.displayName ?? this.label;
  }

  public static cast(obj: any, loaded?: boolean) {
    if (typeof obj === 'object' && obj !== null) {
      const possibleMdfSchema = obj as Partial<MdfEntity>;
      if (possibleMdfSchema.label && possibleMdfSchema.id && (possibleMdfSchema.active !== undefined)) {
        const newForm = new MetaDataSchema(possibleMdfSchema.label, possibleMdfSchema.id, possibleMdfSchema.active);
        if (possibleMdfSchema.flavor) {
          newForm.flavor = possibleMdfSchema.flavor;
        }

        if (possibleMdfSchema.icon) {
          newForm.icon = possibleMdfSchema.icon;
        }

        if (possibleMdfSchema.color) {
          newForm.color = possibleMdfSchema.color;
        }

        newForm.compatibleWithPremiereMarker = possibleMdfSchema.compatibleWithPremiereMarker;

        if (possibleMdfSchema.rule) {
          newForm.rule = possibleMdfSchema.rule;
        }

        if (Array.isArray(possibleMdfSchema.fields)) {
          newForm.fields = possibleMdfSchema.fields;
          newForm.fields.forEach(field => {
            // If a property is not defined, it does not become reactive.
            // MDF editors expect this value present.
            if (isNil(field.defaultValue)) {
              if (field.type === FieldType.Checkbox) {
                // Checkbox fields should have a default value of false
                // From client when we create a new checkbox field, we set default value as false always.
                field.defaultValue = false;
              } else {
                field.defaultValue = '';
              }
            }
          });
        }

        if (possibleMdfSchema.views) {
          newForm.views = possibleMdfSchema.views;
          const id = possibleMdfSchema.id;
          const flavor = possibleMdfSchema.flavor;
          const assignViewIfSupported = (viewType: ViewType) => {
            if (
              doesSchemaSupportView(id, flavor, viewType, possibleMdfSchema.isSubtype) &&
              isEmpty(newForm.views[viewType]) &&
              !isEmpty(newForm.views.item)
            ) {
              newForm.views[viewType] = cloneDeep(newForm.views.item);
            }
          };
          assignViewIfSupported(ViewType.UPLOAD);
          assignViewIfSupported(ViewType.SHARE);
          assignViewIfSupported(ViewType.SCHEDULER);
          assignViewIfSupported(ViewType.COPY_ITEM);
          assignViewIfSupported(ViewType.NEW_VERSION);
          assignViewIfSupported(ViewType.CREATE_PLACEHOLDER);
          assignViewIfSupported(ViewType.FOLDER);
        }

        if (possibleMdfSchema.viewSections) {
          newForm.viewSections = possibleMdfSchema.viewSections;
        }

        if (typeof loaded === 'boolean') {
          newForm.loaded = loaded;
        }

        if (possibleMdfSchema.usageRules) {
          const possibleUsageRules = possibleMdfSchema.usageRules as Partial<TimedMdfUsageRule>[];
          if (isInstanceOfTimedMdfUsageRuleArray(possibleUsageRules)) {
            newForm.usageRules = possibleUsageRules;
          }
        }

        if (possibleMdfSchema.permissions) {
          newForm.permissions = possibleMdfSchema.permissions;
        }

        if (possibleMdfSchema.visibleTo) {
          newForm.visibleTo = possibleMdfSchema.visibleTo;
        }

        if (possibleMdfSchema.isSubtype) {
          newForm.isSubtype = possibleMdfSchema.isSubtype;
        }

        if (possibleMdfSchema.keyboardShortcut) {
          newForm.keyboardShortcut = possibleMdfSchema.keyboardShortcut;
        }

        if (possibleMdfSchema.duration) {
          newForm.duration = possibleMdfSchema.duration;
        }
        if (possibleMdfSchema.displayName) {
          newForm.displayName = possibleMdfSchema.displayName;
        }

        return newForm;
      }
    }
    throw new Error('CastException - Missing expected properties while creating MetaDataform');
  }

  /**
   * Set a new field id. Important to use this function so it's kept in sync
   * with the fieldId saved as part of the LayoutSettings and permissions.
   */
  setFieldId(field: MdfFieldEntity, val: string) {
    if (!field || !val) {
      return;
    }

    const f = this.getField(field.id);
    if (!f) {
      return;
    }

    if (f.fieldId === val) return;
    // Refuse duplicates so another field's permissions are not overwritten.
    if (this.fields.some(existingField => existingField !== f && existingField.fieldId === val)) return;

    const previousFieldId = f.fieldId;

    const movePermissions = (permissions: Record<string, string[]>) => {
      const groups = permissions[previousFieldId];
      delete permissions[previousFieldId];
      if (groups !== undefined) {
        permissions[val] = groups;
      }
    };

    movePermissions(this.permissions.read);
    movePermissions(this.permissions.write);

    f.fieldId = val;

    for (const view in this.views) {
      const settings = this.getSettingsById(f, view as ViewType);
      if (settings) {
        settings.fieldId = val;
      }
    }
  }

  /**
   * Sanitizes the incoming values object against this schema's fields.
   *
   * It will for now:
   *
   * 1) Ignore fields that are present as expected
   * 2) Add default value by field id if the field id is not present in the formData
   * 3) Update the form id to this schema
   * 4) if no value is found, an empty string will be set to conform to Vue's reactivity model.
   */
  sanitize(
    optionLists: OptionListDto[],
    values: ItemMetaData,
    isSubType: boolean,
    viewType?: ViewType,
    customSchema?: MetaDataSchema,
  ): MetadataErrors {
    if (!values || !values.formData) {
      return {};
    }
    if (!isSubType) {
      values.formId = this.id;
    }

    const errors: MetadataErrors = {};
    for (const field of this.fields) {
      let formFieldValue = values.formData[field.id];

      // Skip validation for readonly fields or fields without permissions
      // Exception: SubType fields with readonly=true but userCanRead=true need default values set
      // so that child fields can be resolved before UI renders
      if ((field.type !== FieldType.SubType && field.readOnly === true) || field.userCanWrite === false || field.userCanRead === false) {
        continue;
      }

      const label = this.getSettingsByFieldId(field, viewType ?? ViewType.ITEM)?.label ?? field.fieldId;
      if (!isUndefined(formFieldValue) && formFieldValue !== null && !(field.type === FieldType.MultipleChoice && isEmpty(formFieldValue))) {
        if (field.type === FieldType.Choice) {
          let alternatives: Alternative[] | undefined;
          if (field.optionListId) {
            const optionList = optionLists.find(l => l.id === field.optionListId);
            alternatives = optionList ? getChoiceAlternatives(optionList) : undefined;
          } else {
            alternatives = field.alternatives;
          }
          if (validateChoiceValue(field, alternatives, formFieldValue)) {
            continue;
          }
          errors[field.id] = {
            label,
            errors: ['Invalid value found - please select a new one.'],
          };
        } else if (field.type === FieldType.Text) {
          const result = validateMinMaxCharacters(field, formFieldValue);
          if (result !== true) {
            errors[field.id] = {
              label,
              errors: [result],
            };
          }
        } else if(field.type === FieldType.TreeChoice) {
          let treeAlternatives: string[][] | undefined;
          if (field.optionListId) {
            const optionList = optionLists.find(l => l.id === field.optionListId);
            treeAlternatives = optionList ? getTreeChoiceAlternatives(optionList) : undefined;
          } else {
            treeAlternatives = field.treeAlternatives;
          }
          if (validateTreeChoiceValue(field, treeAlternatives, formFieldValue)) {
            continue;
          }
          errors[field.id] = {
            label,
            errors: ['Invalid value found - please select a new one.'],
          };
        }
      } else {
        // When sanitizing default schema from combinedSanitize function we pass custom schema if present.
        // So that we can consider custom schema layout defaultValue for common fields.
        formFieldValue = resolveFieldDefaultValue(this, field, viewType, customSchema);
        values.formData[field.id] = formFieldValue;
      }

      if (field.required) {
        if (validateRequired(formFieldValue)) {
          continue;
        }
        errors[field.id] = {
          label,
          errors: ['Value required'],
        };
      }
    }

    return errors;
  }

  /**
   * Whether this schema is the default schema.
   */
  get isDefault(): boolean {
    return this.id === DEFAULT_MDF_ID;
  }

  /**
   * Reset the layout settings for this schema for the given type.
   * @param type
   */
  public resetLayoutSettings(defaultSchema: MetaDataSchema | null, type: ViewType) {
    const viewSanitizer = SchemaViewSanitizer.getSchemaViewSanitizer(this, defaultSchema);
    const settings: LayoutSettings[] = viewSanitizer.getResettedLayoutSettingsForView(type);
    this.views[type] = settings;
  }

  /**
   * Reset layout settings back to default for the given field, in all views.
   * @param field The field to reset the settings for
   */
  public resetLayoutSettingsFor(field: MdfFieldEntity) {
    for (const view in this.views) {

      const newSettings = createDefaultSettings(field, view as ViewType, this.label);
      const viewObj = this.views[view as ViewType];

      let index = -1;
      for (let i = 0; i < viewObj.length; i++) {
        if (viewObj[i]?.id === field.id) {
          index = i;
          break;
        }
      }

      // remove old settings (if found)
      if (index >= 0) {
        viewObj.splice(index, 1);
      }

      // push new
      if (shouldFieldTypeBeInView(field.type, view as ViewType)) {
        viewObj.push(newSettings);
      }
    }
  }

  /**
   * Copy the layout settings from one view to another, We don't allow copying between layouts for
   * default form due to the complexity of hardcoded search fields in the advanced search menu.
   *
   * @param fromType: The view type to copy the settings from
   * @param toType: The view type to copy the settings to
   */
  public copyLayoutSettings(defaultSchema: MetaDataSchema | undefined, fromType: ViewType, toType: ViewType) {
    if (this.id === DEFAULT_MDF_ID) {
      return;
    }
    const newSettings: LayoutSettings[] = [];
    const customFieldsByFieldId = keyBy(this.fields, (f) => f.fieldId);
    const defaultFieldsByFieldId = keyBy(defaultSchema?.fields ?? [], (f) => f.fieldId);

    // It's important we copy the objects, and not directly assign, otherwise
    // it's copied by reference which we do not want.
    for (const settings of this.views[fromType]) {
      const field = customFieldsByFieldId[settings.fieldId] ?? defaultFieldsByFieldId[settings.fieldId];
      if (!field) {
        continue;
      }
      if (shouldFieldTypeBeInView(field.type, toType)) {
        newSettings.push(Object.assign({}, settings));
      }
    }
    this.views[toType] = newSettings;
  }

  /**
   * Get field by ID
   * @param id the id of the field to fetch
   */
  public getField(idOrFieldId: string): MdfFieldEntity | null {
    for (const field of this.fields) {
      if (field.id === idOrFieldId || field.fieldId === idOrFieldId) {
        return field;
      }
    }
    return null;
  }

  /**
   * Add a new field to the schema.
   * @param fieldId The field id of the field.
   * @param type Which field type to create
   * @param defaultValue optional default value of the field.
   *
   * This will also generate the appropriate default layout settings for this field. Do not push MetaDataFields
   * directly to the fields array, then this step is skipped.
   */
  public addField(fieldId: string, type: FieldType, defaultValue?: string | boolean | number): MetaDataFieldImpl {
    const field = new MetaDataFieldImpl(fieldId, type, defaultValue ?? '');
    this.fields.push(field);
    // Create layout settings for the new field
    for (const view in this.views) {
      if (shouldFieldTypeBeInView(type, view as ViewType)) {
        const viewSpecificSettings = createDefaultSettings(field, view as ViewType, this.label);
        this.views[view as ViewType].push(viewSpecificSettings);
      }
    }
    return field;
  }

  /**
   * Removes, if found, the given field from this schema.
   *
   * It will also remove permissions, layout settings, and section references associated with this field.
   */
  public removeField(fieldOrIndex: MdfFieldEntity | number | string) {
    let index = -1;
    if (typeof fieldOrIndex === 'number') {
      index = fieldOrIndex;
    } else if (typeof fieldOrIndex === 'string') {
      const field = this.getField(fieldOrIndex);
      if (field) {
        index = this.fields.indexOf(field, 0);
      }
    } else {
      index = this.fields.indexOf(fieldOrIndex, 0);
    }

    if (index > -1) {
      const field = this.fields[index]!;
      if (field?.systemDefaultField) {
        return;
      }

      delete this.permissions.read[field.fieldId];
      delete this.permissions.write[field.fieldId];

      // Clean up any section references to the deleted field before removing layout settings
      // so the cleanup method can still find the field's position in the view settings
      this.cleanupSectionReferencesOnFieldDeletion([field.fieldId]);

      // We should delete all the associated layout settings
      // before removing the actual field to avoid concurrency issues
      // in the layout settings editor.
      for (const key in this.views) {
        const view = this.views[key as ViewType];
        for (let i = 0; i < view.length; i++) {
          const settings = view[i];
          if (settings?.fieldId === field.fieldId) {
            view.splice(i, 1);
            break;
          }
        }
      }

      this.fields.splice(index, 1);
    }
  }

  /**
   * Get all visible fields for this schema.
   * @param viewType The viewtype to get the visible fields for
   */
  public getVisibleFields(viewType: ViewType): MdfFieldEntity[] {
    return this.fields.filter((f: MdfFieldEntity) => {
      if (f.systemDefaultField === true) {
        return false;
      }

      const layoutSettings = this.getSettingsByFieldId(f, viewType);
      if (layoutSettings && layoutSettings.visible === false) {
        return false;
      }
      return true;
    });
  }

  /**
   * Returns the field object by id, and also includes the layout settings for the given type.
   * @param id The field id to fetch
   * @param type For which {@link ViewType} to fetch {@link LayoutSettings} for
   */
  public getFieldAndSettings(id: string, type: ViewType): MdfAndSettings | null {
    for (const field of this.fields) {
      if (field.id === id) {
        const existingSettings = this.getSettingsByFieldId(field, type);
        return { ...field, settings: existingSettings || createDefaultSettings(field, type)};
      }
    }
    return null;
  }

  /**
   * Retrieve the layout settings for the given field | field.id
   *
   * Prefer { @see getSettingsByFieldId }, this function only exists to fetch by id in editing fieldId workflows.
   *
   * @param field Actual field or id to fetch the settings for
   * @param type For which viewtype to fetch settings for
   */
  public getSettingsById(field: MdfFieldEntity, type: ViewType): LayoutSettings | null {
    return this.getSettings(field, type, 'id');
  }

  /**
   * Retrieve the layout settings for the given field | field.fieldId
   * @param field Actual field or fieldid to fetch the settings for
   * @param type For which viewtype to fetch settings for
   */
  public getSettingsByFieldId(field: MdfFieldEntity, type: ViewType): LayoutSettings | null {
    return this.getSettings(field, type, 'fieldId');
  }

  public getSettings(field: MdfFieldEntity | string, type: ViewType, idOrFieldId: 'id' | 'fieldId') {
    const id = typeof field === 'string' ? field : field[idOrFieldId];
    const settings = this.views[type];
    for (const setting of settings) {
      if (setting[idOrFieldId] === id) {
        return setting;
      }
    }
    return null;
  }

  public isSubtypeMdf() {
    return this.isSubtype === true;
  }

  /**
   * Add a new section to the schema.
   * @param sectionId The section id of the field.
   * @param viewType Which view type to create
   * @param placeAboveFieldId The field id of the field above which this section should be placed.
   * @param label optional label of the section.
   * @param sortOrder Optional sort order for sections placed above the same field.
   */
  public addSection(sectionId: string, viewType: ViewType.ITEM, placeAboveFieldId?: string, label?: string, sortOrder?: number) {
    /* TODO: Add support for more viewTypes by removing ViewType.ITEM type constraint */
    const defaultSection: LayoutSection = {
      placeAboveFieldId: placeAboveFieldId ?? '', // Used to place section above a field
      sectionId,
      label: label ?? sectionId,
      sortOrder,
    };
    const viewSection = this.viewSections[viewType];
    if(!viewSection) {
      this.viewSections[viewType] = [defaultSection];
    } else {
      viewSection.push(defaultSection);
    }
  }

  /**
   * Remove a section from the schema.
   * @param sectionId The section id of the field.
   * @param viewType the viewType to remove the section from.
   */
  public removeSection(sectionId: string, viewType: ViewType.ITEM) {
    /* TODO: Add support for more viewTypes by removing ViewType.ITEM type constraint */
    const view = this.viewSections[viewType];
    if(!view) {
      return;
    }
    const index = view.findIndex(section => section.sectionId === sectionId);
    if (index > -1) {
      view.splice(index, 1);
    }
  }

  /**
   * Clean up viewSection references when fields are deleted.
   * This utility ensures that when a field is deleted, any sections in viewSections
   * that reference the deleted field (via placeAboveFieldId) are updated to point to
   * a new valid field or have their reference cleared if no valid field exists.
   *
   * @param deletedFieldIds Array of field IDs that have been deleted
   * @returns Object containing the number of sections updated and their details
   */
  public cleanupSectionReferencesOnFieldDeletion(
    deletedFieldIds: string[]
  ): { updatedSections: number; sectionsUpdated: Array<{ sectionId: string; oldFieldId: string; newFieldId: string | null }> } {
    const result = {
      updatedSections: 0,
      sectionsUpdated: [] as Array<{ sectionId: string; oldFieldId: string; newFieldId: string | null }>
    };

    if (!deletedFieldIds.length || !this.viewSections[ViewType.ITEM]) {
      return result;
    }

    const deletedFieldIdSet = new Set(deletedFieldIds);
    const sections = this.viewSections[ViewType.ITEM];

    const remainingFieldIds = this.fields
      .filter(field => !deletedFieldIdSet.has(field.fieldId))
      .map(field => field.fieldId);

    const findReplacementField = (originalFieldId: string): string | null => {
      if (!remainingFieldIds.length) {
        return null;
      }

      const currentViewSettings = this.views[ViewType.ITEM] || [];
      const originalIndex = currentViewSettings.findIndex(setting => setting.fieldId === originalFieldId);

      if (originalIndex >= 0) {
        for (let i = originalIndex + 1; i < currentViewSettings.length; i++) {
          const candidateFieldId = currentViewSettings[i]!.fieldId;
          if (!deletedFieldIdSet.has(candidateFieldId)) {
            return candidateFieldId;
          }
        }
      }

      return null;
    };

    sections.forEach((section: LayoutSection) => {
      if (section.placeAboveFieldId && deletedFieldIdSet.has(section.placeAboveFieldId)) {
        const oldFieldId = section.placeAboveFieldId;
        const newFieldId = findReplacementField(oldFieldId);

        section.placeAboveFieldId = newFieldId || '';

        result.updatedSections++;
        result.sectionsUpdated.push({
          sectionId: section.sectionId,
          oldFieldId,
          newFieldId
        });
      }
    });

    return result;
  }
}

export function buildMdfDisplayNameMap(schemas: MetaDataSchema[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const mdf of schemas) {
    map[mdf.label] = mdf.resolvedDisplayName;
  }

  return map;
}

export const normalizeFieldsBeforeSave = (schema: MetaDataSchema) => {
  if (!schema || !schema.fields) {
    return;
  }

  // Make sure to not send a "fake" field id to the server,
  // let the server generate it instead.
  for (const field of schema.fields) {
    if (!isSavedField(field)) {
      // "as any" disables a ts error about not allowing to delete a non-optional field.
      // This would require cleanup across lambda and MDF that is not worth it considering
      // time spent vs gain.
      delete (field as any).id;
    }
  }
};

// When we change mdf type, We should reset defaultValues by following this priority order -
// current schema layoutSettings > common layoutSettings > common model.
function setCommonFieldsDefaultValue(
  defaultSchema: MetaDataSchema,
  schema: MetaDataSchema | undefined | null,
  values: ItemMetaData,
  viewType: ViewType,
  prevSchema?: MetaDataSchema,
  latestSnapshot?: ItemMetaData | null,
) {
  defaultSchema.fields.forEach(field => {
    const currentValue = values.formData[field.id];
    const prevSchemaViewDefaultValue = prevSchema ? prevSchema.views[viewType].find(t => t.fieldId === field.fieldId)?.defaultValue : undefined;
    const currentSchemaViewDefaultValue = schema ? schema.views[viewType].find(t => t.fieldId === field.fieldId)?.defaultValue : undefined;
    const defaultSchemaViewDefaultValue = defaultSchema.views[viewType].find(t => t.fieldId === field.fieldId)?.defaultValue;

    // If value is already saved in DB then should skip.
    if (latestSnapshot && isEqual(latestSnapshot.formData[field.id], currentValue)) {
      return;
    }

    const defaults = [prevSchemaViewDefaultValue, defaultSchemaViewDefaultValue, field.defaultValue];
    // If value is absent in defaults, that means value is coming from unsaved change, which should not be overridden.
    if (field.type === FieldType.MultipleChoice && !isEmpty(currentValue)) {
      let equal = false;
      defaults.forEach(value => isEqual(value, currentValue) ? equal = true : '');
      if (!equal) {
        return;
      }
    } else if (field.type !== FieldType.MultipleChoice && !defaults.includes(currentValue)) {
      return;
    }

    if (field.type === FieldType.MultipleChoice) {
      // For multiple choice we should consider empty array as undefined and set defaults according to priority order.
      if (currentSchemaViewDefaultValue && !isEmpty(currentSchemaViewDefaultValue)) {
        values.formData[field.id] = currentSchemaViewDefaultValue;
      } else if (defaultSchemaViewDefaultValue && !isEmpty(defaultSchemaViewDefaultValue)) {
        values.formData[field.id] = defaultSchemaViewDefaultValue;
      } else if (field.defaultValue && !isEmpty(field.defaultValue)) {
        values.formData[field.id] = field.defaultValue;
      } else if (prevSchemaViewDefaultValue && !isEmpty(prevSchemaViewDefaultValue)) {
        delete values.formData[field.id];
      }
    } else if (currentSchemaViewDefaultValue) {
      values.formData[field.id] = currentSchemaViewDefaultValue;
    } else if (defaultSchemaViewDefaultValue) {
      values.formData[field.id] = defaultSchemaViewDefaultValue;
    } else if (field.defaultValue) {
      values.formData[field.id] = field?.defaultValue;
    } else if (prevSchemaViewDefaultValue) {
      // If common field does not have default value set in any view and has value in previous schema
      // then we should reset it.
      delete values.formData[field.id];
    }
  });
}

/**
 * Will perform a combined sanitize towards the given schemas & the formData, sets default values & errors, ensuring some edge
 * cases are taken care of. Note that the given values param will be mutated directly.
 * Make sure provided values are correct otherwise the metadata editor will behave unexpectedly.
 * @param optionLists Option lists of choice/multi-choice/freeform fields.
 * @param schema A custom schema.
 * @param defaultSchema The default schema.
 * @param values The values to be sanitized. Mutated directly.
 * @param viewType The view settings should be used (Item, Upload, Scheduler, etc). If not provided then model will be used.
 * @param existingErrors The existing metadata errors.
 * @param prevSchema The previous selected schema. If exist, used to determine if values param contains default value from this schema.
 * @param latestSnapshot The metadata snapshot that is coming from DB. This snapshot does not change until metadata change is saved.
 * @returns MetadataErrors.
 */
export const combinedSanitize = (
  optionLists: OptionListDto[],
  schema: MetaDataSchema | undefined | null,
  defaultSchema: MetaDataSchema | null,
  values: ItemMetaData,
  allMdfs: MetaDataSchema[],
  viewType?: ViewType,
  existingErrors: MetadataErrors = {},
  prevSchema?: MetaDataSchema,
  latestSnapshot?: ItemMetaData | null,
): MetadataErrors => {
  let combinedErrors: MetadataErrors = {};
  const subTypeSchemas: MetaDataSchema[] = [];

  // We copy previous metadata values to selected mdf type. So we need to
  // consider selected mdf's layoutSettings defaultValue for common fields default value. (For regular metadata only)
  if (defaultSchema && viewType && !schema?.flavor) {
    setCommonFieldsDefaultValue(defaultSchema, schema, values, viewType, prevSchema, latestSnapshot);
  }

  // Has to be in this order - so the form id gets updated correctly.
  if (defaultSchema) {
    // If custom schema is present then we should prioritize custom schema layoutSettings defaultValue.
    combinedErrors = { ...defaultSchema.sanitize(optionLists, values, false, viewType, schema ?? undefined) };
    subTypeSchemas.push(...returnSubTypesIfAvailable(defaultSchema, allMdfs, values));
  }

  if (schema) {
    combinedErrors = { ...combinedErrors, ...schema.sanitize(optionLists, values, false, viewType) };
    subTypeSchemas.push(...returnSubTypesIfAvailable(schema, allMdfs, values));
  }

  combinedErrors = { ...combinedErrors, ...recursiveSubtypeSanitize(subTypeSchemas, optionLists, allMdfs, values, viewType)};

  // Preserve existing external errors
  for (const key of Object.keys(existingErrors)) {
    if (existingErrors[key]!.isExternalError) {
      combinedErrors[key] = existingErrors[key]!;
    }
  }

  return combinedErrors;
};

export function recursiveSubtypeSanitize(
  subtypeSchemas: MetaDataSchema[],
  optionLists: OptionListDto[],
  allMdfs: MetaDataSchema[],
  values: ItemMetaData,
  viewType?: ViewType,
) {
  const metadataErrors: MetadataErrors = {};
  const schemas = [...subtypeSchemas];
  const seen: Record<string, boolean> = {};
  // Note that the code block below is "recursive" as the code pushes into the array that the for loop itself
  // is iterating over. This needs to done this way because "subtypeSchema.sanitize" can fill in default values
  // for subtype typed fields as well and _then_ we need to see if there are other subtype schemas referenced in
  // those fields.
  for (const subtypeSchema of schemas) {
    if (seen[subtypeSchema.id]) {
      continue;
    }
    Object.assign(metadataErrors, subtypeSchema.sanitize(optionLists, values, true, viewType));
    schemas.push(...returnSubTypesIfAvailable(subtypeSchema, allMdfs, values));
    seen[subtypeSchema.id] = true;
  }

  return metadataErrors;
}

/**
 * Helper class for sanitizing views of schemas
 * For default schema or schemas with flavor (e.g., timed or technical), we need to sanitize based on their fields only
 * For flavorless schemas, we need to include default schema's settings in their views along with their own fields' settings (except for search view)
 */
export class SchemaViewSanitizer {
  private defaultSchemaFieldsMap: Dictionary<MdfFieldEntity> = {};
  private customSchemaFieldsMap: Dictionary<MdfFieldEntity> = {};

  private constructor(private defaultSchema: MetaDataSchema | null, private customSchema: MetaDataSchema | null) {
    this.defaultSchemaFieldsMap = keyBy(this.defaultSchema ? this.defaultSchema.fields : [], f => f.fieldId);
    this.customSchemaFieldsMap = keyBy(this.customSchema ? this.customSchema.fields : [], f => f.fieldId);
  }

  /*
    Use this when you want the class to take care of whether to include default fields
    when sanitizing the views. so defaultSchema should be passed here but we type it as MetaDataSchema | undefined
    because this.$store.getters.defaultMdf is typed as MetaDataSchema | undefined

    For substypes, the class will not set default schema as we do not want to include default fields
    when sanitizing the views.
  */
  public static getSchemaViewSanitizer(schemaToSanitize: MetaDataSchema, defaultSchema: MetaDataSchema | null) {
    if (schemaToSanitize.id === DEFAULT_MDF_ID) {
      return new SchemaViewSanitizer(schemaToSanitize, null);
    }
    // subtype, folder common and schemas with flavor does not have default schema fields in views.
    // That is why we should not provide default schema here.
    else if (schemaToSanitize.flavor || schemaToSanitize.isSubtypeMdf() || schemaToSanitize.id === FOLDER_DEFAULT_MDF_ID) {
      return new SchemaViewSanitizer(null, schemaToSanitize);
    }
    return new SchemaViewSanitizer(defaultSchema ?? null, schemaToSanitize);
  }

  // Use this when you want the return values of the apis of this class
  // to not include default mdf fields or settings explicitly even for flavorless custom schemas
  public static excludeDefault(schema: MetaDataSchema) {
    return SchemaViewSanitizer.getSchemaViewSanitizer(schema, null);
  }

  public getSanitizedLayoutSettingsForView(viewType: ViewType) {
    const layoutSettings = this.getLayoutSettings(viewType);
    for (const setting of layoutSettings) {
      if (this.defaultSchemaFieldsMap[setting.fieldId]) {
        setting.mdfSource = 'Common';
      } else if (this.customSchema) {
        setting.mdfSource = this.customSchema.label;
      }

      // This sanitized view would be saved to DB if user makes any change.
      // So defaultValue should be set by following proper order (For regular metadata only).
      // NOTE: Here we only set common fields defaultValue in custom schema view.
      if (
        (setting.mdfSource === 'Common' && this.customSchema) &&
        (setting.defaultValue === undefined || (this.defaultSchemaFieldsMap[setting.fieldId]?.type === FieldType.MultipleChoice && isEmpty(setting.defaultValue))) &&
        !this.customSchema.flavor
      ) {
        const viewDefaultValue = this.defaultSchema?.views[viewType].find(field => field.fieldId === setting.fieldId)?.defaultValue;
        const modelDefaultValue =  this.defaultSchemaFieldsMap[setting.fieldId]?.defaultValue;

        if (this.defaultSchemaFieldsMap[setting.fieldId]?.type === FieldType.MultipleChoice) {
          setting.defaultValue = !isEmpty(viewDefaultValue) ? viewDefaultValue : modelDefaultValue;
        } else {
          setting.defaultValue = viewDefaultValue ?? modelDefaultValue;
        }
      }
    }
    return layoutSettings;
  }

  private getLayoutSettings(viewType: ViewType) {
    if (this.customSchema) {
      return this.getValidLayoutSettingsCustomSchema(viewType, this.customSchema);
    } else if (this.defaultSchema) {
      return this.getValidLayoutSettingsDefaultSchema(viewType);
    }

    return [];
  }

  private getValidLayoutSettingsDefaultSchema(viewType: ViewType) {
    invariant(this.defaultSchema, 'Expecting default schema');

    const defaultSchemaViewSettingsMap = keyBy(this.defaultSchema.views[viewType], f => f.fieldId);
    const isValidSetting = (setting: LayoutSettings) => {
      const field: MdfFieldEntity | undefined = this.defaultSchemaFieldsMap[setting.fieldId]!;
      return field && shouldFieldTypeBeInView(field.type, viewType);
    };

    const shouldAddSettingForField = (field: MdfFieldEntity) => {
      return !defaultSchemaViewSettingsMap[field.fieldId] && shouldFieldTypeBeInView(field.type, viewType);
    };

    return this.getValidLayoutSettings(this.defaultSchema, viewType, isValidSetting, shouldAddSettingForField);
  }

  // TODO: Simplify this function
  private getValidLayoutSettingsCustomSchema(viewType: ViewType, schema: MetaDataSchema) {
    invariant(schema, 'Expected custom schema to exist');

    const schemaViewSettingsMap = keyBy(schema.views[viewType], f => f.fieldId);
    const schemaFieldsMappedByFieldId = keyBy(schema.fields, f => f.fieldId);

    const isValidSetting = (setting: LayoutSettings) => {
      const field: MdfFieldEntity | undefined = this.defaultSchemaFieldsMap[setting.fieldId] ?? schemaFieldsMappedByFieldId[setting.fieldId];
      return field !== undefined && shouldFieldTypeBeInView(field.type, viewType);
    };

    const shouldAddSettingForField = (field: MdfFieldEntity) => {
      return !schemaViewSettingsMap[field.fieldId] && shouldFieldTypeBeInView(field.type, viewType);
    };

    const layoutSettings = this.getValidLayoutSettings(schema, viewType, isValidSetting, shouldAddSettingForField);

    // We include default schema field views only for flavorless custom schemas (as flavored schemas don't really have a 'default' schema)
    // We also don't include default schema fields for search view as it is special handled in advanced search menu
    if (this.defaultSchema && !schema.flavor && viewType !== ViewType.SEARCH) {
      const doesCustomSchemaViewHaveDefaultSetting = schema.views[viewType].find(s => !!this.defaultSchemaFieldsMap[s.fieldId]);

      if (!doesCustomSchemaViewHaveDefaultSetting) {
        // if there is no default mdf setting in view, then we add copies of all the default mdf view at the front for backward compatibility
        const defaultLayoutSettings = this.getValidLayoutSettingsDefaultSchema(viewType);
        for (let i = defaultLayoutSettings.length - 1; i >= 0; --i) {
          layoutSettings.unshift(cloneDeep(defaultLayoutSettings[i]!));
        }
      } else {
        // otherwise, we append the missing ones at the end
        const defaultSchemaViewSettingsMap = keyBy(this.defaultSchema.views[viewType], f => f.fieldId);
        for (const field of this.defaultSchema.fields) {
          if (shouldAddSettingForField(field)) {
            layoutSettings.push(defaultSchemaViewSettingsMap[field.fieldId] ? cloneDeep(defaultSchemaViewSettingsMap[field.fieldId]!) : createDefaultSettings(field, viewType, 'Common'));
          }
        }
      }
    }

    return layoutSettings;
  }

  private getValidLayoutSettings(
    schema: MetaDataSchema, viewType: ViewType,
    isValidSetting: (setting: LayoutSettings) => boolean,
    shouldAddSettingForField: (field: MdfFieldEntity) => boolean,
  ) {
    const layoutSettings: LayoutSettings[] = [];

    for (const setting of schema.views[viewType] ?? []) {
      if (isValidSetting(setting)) {
        layoutSettings.push(setting);
      }
    }

    for (const field of schema.fields) {
      // We only sanitize non system default fields in search, since those fields
      // are hardcoded in the advanced search menu and can't be controlled by the user.
      if (viewType === ViewType.SEARCH && field.systemDefaultField) {
        continue;
      }
      if (shouldAddSettingForField(field)) {
        layoutSettings.push(createDefaultSettings(field, viewType, schema.label));
      }
    }

    return layoutSettings;
  }

  public getResettedLayoutSettingsForView(viewType: ViewType) {
    const layoutSettings: LayoutSettings[] = [];

    if (this.defaultSchema && !this.customSchema?.flavor) {
      for (const field of this.defaultSchema.fields) {
        if (viewType === ViewType.SEARCH && (field.systemDefaultField || this.customSchema)) {
          continue;
        }
        if (shouldFieldTypeBeInView(field.type, viewType)) {
          layoutSettings.push(createDefaultSettings(field, viewType, 'Common'));
        }
      }
    }

    if (this.customSchema) {
      for (const field of this.customSchema.fields) {
        if (shouldFieldTypeBeInView(field.type, viewType)) {
          layoutSettings.push(createDefaultSettings(field, viewType, this.customSchema.label));
        }
      }
    }

    return layoutSettings;
  }

  public getFieldsAndSettingsForView(viewType: ViewType, allMdfs: MetaDataSchema[], formData: Record<string, FieldValue>) {
    const layoutSettings = this.getLayoutSettings(viewType);
    const fieldsAndSettings: MdfAndSettings[] = [];
    for (const setting of layoutSettings) {
      if (this.defaultSchemaFieldsMap[setting.fieldId] || this.customSchemaFieldsMap[setting.fieldId]) {
        const fieldAndSetting: MdfAndSettings  = { settings: setting, ...(this.defaultSchemaFieldsMap[setting.fieldId] ?? this.customSchemaFieldsMap[setting.fieldId]!) };
        fieldsAndSettings.push(fieldAndSetting);
        if (fieldAndSetting.type == FieldType.SubType) {
          const subfields = this.getFieldsForSubtype(viewType, allMdfs, formData, fieldAndSetting.id);
          fieldsAndSettings.push(...subfields);
        }
      }
    }
    return fieldsAndSettings;
  }

  private getMdfForLabel(allMdfs: MetaDataSchema[], label: string) {
    return allMdfs.find(mdf => mdf.label === label);
  }

  // TODO: Remove duplication
  private getFieldsForSubtype(viewType: ViewType, allMdfs: MetaDataSchema[], formData: Record<string, FieldValue>, fieldGuid: string) {
    const fieldsAndSettings: MdfAndSettings[] = [];
    if (isEmpty(formData)) {
      return fieldsAndSettings;
    }
    const subtype_id = formData[fieldGuid];
    if (typeof subtype_id === 'string') {
      const mdf = this.getMdfForLabel(allMdfs, subtype_id);
      if (mdf) {
        const layoutSettings = this.getValidLayoutSettingsCustomSchema(viewType, mdf);
        const fieldsMappedById = keyBy(mdf.fields, f => f.fieldId);
        for (const setting of layoutSettings) {
          if (fieldsMappedById[setting.fieldId]) {
            const fieldAndSetting: MdfAndSettings  = { settings: setting, ...fieldsMappedById[setting.fieldId]! };
            fieldsAndSettings.push(fieldAndSetting);
            if (fieldAndSetting.type == FieldType.SubType) {
              const subfields = this.getFieldsForSubtype(viewType, allMdfs, formData, fieldAndSetting.id);
              fieldsAndSettings.push(...subfields);
            }
          }
        }
      }
    }

    return fieldsAndSettings;
  }
}

/**
 * When copying metadata from another item the readOnly fields should not be updated.
 * @param incomingMetadata metadata coming from another item
 * @param existingMetadata metadata present to the placeholder item
 * @param defaultSchema MetaDataSchema
 * @param incomingMetadataSchema schema of incoming metadata.
 * @returns ItemMetadata
 */
export const avoidReadOnlyFields = (incomingMetadata: ItemMetaData, existingMetadata: ItemMetaData | null, defaultSchema: MetaDataSchema, incomingMetadataSchema: MetaDataSchema) => {
  if (!existingMetadata?.formId) {
    return incomingMetadata;
  }

  const metadata: ItemMetaData = {formId: incomingMetadata.formId, formData: incomingMetadata.formData};
  defaultSchema.fields.forEach(field => {
    if (field.readOnly) {
      metadata.formData[field.id] = existingMetadata.formData[field.id]!;
    }
  });

  incomingMetadataSchema.fields.forEach(field => {
    if (field.readOnly && existingMetadata.formId === incomingMetadata.formId) {
      metadata.formData[field.id] = existingMetadata.formData[field.id]!;
    }
  });
  return metadata;
};

/**
 * Discards previous custom schema fields from itemMetadata if -
 * prevSchema field does not have a fieldID that is present in selectedSchema and of same type.
 * @param prevSchema schema that was selected before and did not saved.
 * @param selectedSchema selected schema.
 * @param data ItemMetadata
 * @returns metadata object
 */
export function discardPreviousCustomSchemaFields(prevSchema: MetaDataSchema, selectedSchema: MetaDataSchema, data: ItemMetaData) {
  // Only discard fields when prevSchema is not default schema and current schema is different.
  if (prevSchema.isDefault || (prevSchema.id === data.formId)) {
    return data;
  }

  const fieldsById = keyBy(prevSchema.fields, 'id');

  for (const id in data.formData) {
    const prevSchemaField = fieldsById[id];
    if (!prevSchemaField) {
      continue;
    }

    const selectedSchemaField = selectedSchema.getField(prevSchemaField.fieldId);
    if (selectedSchemaField && selectedSchemaField.type === prevSchemaField.type) {
      // Two custom schema might have field of same fieldId. In this case user wants to see the value
      // of previous schema is persisted before save in client. To ensure that we check fieldId and type compatibility here.
      data.formData[selectedSchemaField.id] = data.formData[id]!;
      delete data.formData[id];
    } else {
      delete data.formData[id];
    }
  }
  return data;
}

export function updatedMetadataWithDateInitializationRules(mdf: MdfSchemaNoViews | null | undefined, metadata: ItemMetaData) {
  if (mdf === null || mdf === undefined || !mdf.fields) {
    return;
  }
  const mdfFields = mdf.fields.filter(field => field.type === FieldType.Date);
  mdfFields.forEach(field => {
    const dateRule = field.initializationRules ? field.initializationRules.find(isDateOffsetRule) : undefined;
    const currentValue = metadata.formData[field.id];
    const needsInitialization = currentValue === undefined || currentValue === '';
    if (dateRule && needsInitialization) {
      metadata.formData[field.id] = getDateWithOffset(dateRule.offsetDays, field.defaultValue);
    }
  });
}

export function applyDateOffsetInitializationRule(metadata: ItemMetaData, usedSchemas: MdfSchemaNoViews[]) {
  for (const schema of usedSchemas) {
    updatedMetadataWithDateInitializationRules(schema, metadata);
  }
}

export function returnSubTypesIfAvailable(schema: MetaDataSchema, allMdfs: MetaDataSchema[], metadata: ItemMetaData) {
  const subTypeSchemas: MetaDataSchema[] = [];
  schema.fields.forEach(field => {
    if (field.type !== FieldType.SubType) {
      return;
    }
    const schemaLabel = metadata.formData[field.id];
    const subTypeSchema = allMdfs.find(mdf => mdf.label === schemaLabel);
    if (!subTypeSchema) {
      return;
    }
    subTypeSchemas.push(subTypeSchema);
  });
  return subTypeSchemas;
}

export function returnLayoutSettingsWithSections(schema: MetaDataSchema, settings: LayoutSettings[]): (LayoutSection | LayoutSettings)[] {
  const viewSections = schema.viewSections[ViewType.ITEM] ?? [];
  const fieldIds = new Set(settings.map(s => s.fieldId));

  // Groups sections by their placeAboveFieldId and handle multiple sections per field
  const sectionsMap = new Map<string, LayoutSection[]>();
  const unplacedSections: LayoutSection[] = [];

  viewSections.forEach(section => {
    if (section.placeAboveFieldId && fieldIds.has(section.placeAboveFieldId)) {
      // Group sections that target the same field
      if (!sectionsMap.has(section.placeAboveFieldId)) {
        sectionsMap.set(section.placeAboveFieldId, []);
      }
      sectionsMap.get(section.placeAboveFieldId)!.push(section);
    } else {
      unplacedSections.push(section);
    }
  });

  sectionsMap.forEach(sectionGroup => {
    sectionGroup.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  });

  // Build the combined list
  const combinedList: (LayoutSection | LayoutSettings)[] = [];

  settings.forEach(setting => {
    // Add all sections that should be placed above this field
    const sectionsForField = sectionsMap.get(setting.fieldId) || [];
    sectionsForField.forEach(section => combinedList.push(section));

    // Add the field
    combinedList.push(setting);
  });

  // Add unplaced sections at the end
  unplacedSections.forEach(section => combinedList.push(section));

  return combinedList;
}
